import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDaysToDate, formatDateLong } from "../Oncall/utils";
import PatientSummaryTable from "../Oncall/PatientTable";
import { updateDoctorFields } from "../../utils/functions";
import { isCapabilityEnabled } from "../../config/capabilities";
import CopyValueButton from "./CopyValueButton";
import { getDoctorByUserId, getPatientsByDoctorAndDate } from "./patientApi";
import { supportPatientPath } from "./supportUrls";
import {
  supportContextCardTitle,
  supportSubpanel,
  supportSubpanelHeader,
} from "./supportStyles";

const MODEL_TYPE_OPTIONS = [
  { value: "", label: "Select Model Type" },
  { value: "Multilingual", label: "Multilingual" },
  { value: "English", label: "English" },
];

const DetailRowShell = ({ label, children }) => (
  <div className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3 items-center py-1">
    <dt className="text-[11px] text-gray-500 leading-snug">{label}</dt>
    <dd className="text-sm font-medium text-gray-900 break-words min-w-0 leading-snug">
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">{children}</span>
    </dd>
  </div>
);

const DetailRow = ({ label, value, children }) => (
  <DetailRowShell label={label}>
    <span>{value === null || value === undefined || value === "" ? "—" : value}</span>
    {children}
  </DetailRowShell>
);

const DoctorPatientPicker = ({
  doctor,
  doctorDate,
  onDateChange,
  onChangeDoctor,
  selectedPatientId = null,
}) => {
  const navigate = useNavigate();
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [isLoadingDoctorDetails, setIsLoadingDoctorDetails] = useState(false);
  const [isSavingModelType, setIsSavingModelType] = useState(false);
  const [isEditingModelType, setIsEditingModelType] = useState(false);
  const [pendingModelType, setPendingModelType] = useState("");
  const [doctorPatients, setDoctorPatients] = useState([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);

  useEffect(() => {
    setIsEditingModelType(false);
    setPendingModelType("");
  }, [doctor?.user_id]);

  useEffect(() => {
    if (!doctor?.user_id) {
      setDoctorDetails(null);
      return;
    }

    let cancelled = false;
    setIsLoadingDoctorDetails(true);
    setDoctorDetails(null);

    (async () => {
      const data = await getDoctorByUserId(doctor.user_id);
      if (cancelled) return;
      if (!data.error) {
        setDoctorDetails(data);
      }
      setIsLoadingDoctorDetails(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [doctor?.user_id]);

  useEffect(() => {
    if (!doctor?.user_id || !doctorDate) {
      setDoctorPatients([]);
      return;
    }

    setIsLoadingPatients(true);
    setDoctorPatients([]);
    (async () => {
      try {
        const formattedDate = formatDateLong(doctorDate);
        const patients = await getPatientsByDoctorAndDate(doctor.user_id, formattedDate);
        setDoctorPatients(patients);
      } finally {
        setIsLoadingPatients(false);
      }
    })();
  }, [doctor, doctorDate]);

  const getModelTypeDisplay = (value) => {
    if (!value) return "Not set";
    const option = MODEL_TYPE_OPTIONS.find((o) => o.value === value);
    return option?.label || value;
  };

  const handleStartModelTypeEdit = () => {
    setPendingModelType(doctorDetails?.model_type || "");
    setIsEditingModelType(true);
  };

  const handleCancelModelTypeEdit = () => {
    setPendingModelType(doctorDetails?.model_type || "");
    setIsEditingModelType(false);
  };

  const handleSaveModelType = async () => {
    if (!doctorDetails) return;

    const previousDetails = doctorDetails;
    const updatedDetails = { ...doctorDetails, model_type: pendingModelType };
    setIsSavingModelType(true);

    try {
      const currentDatabase = localStorage.getItem("currentDatabase");
      const username = localStorage.getItem("username");
      const saved = await updateDoctorFields(
        { ...updatedDetails, username },
        currentDatabase
      );
      if (saved) {
        setDoctorDetails({
          ...saved,
          onco_emr_url: saved.onco_emr_url ?? previousDetails.onco_emr_url,
        });
        setIsEditingModelType(false);
      } else {
        setDoctorDetails(previousDetails);
      }
    } finally {
      setIsSavingModelType(false);
    }
  };

  const handlePatientClick = (patientId) => {
    navigate(
      supportPatientPath(patientId, {
        doctorId: doctor.user_id,
        date: doctorDate,
      })
    );
  };

  const formattedDisplayDate = doctorDate
    ? new Date(doctorDate + "T12:00:00").toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-1 border-b border-gray-200">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Selected doctor
          </p>
          <p className="text-lg font-semibold text-gray-900">{doctor.user_name}</p>
        </div>
        <button
          type="button"
          className="text-sm font-medium text-ktx-purple hover:underline"
          onClick={onChangeDoctor}
        >
          Change doctor
        </button>
      </div>

      <section className={supportSubpanel} aria-label="Doctor details">
        <div className={supportSubpanelHeader}>
          <h3 className={supportContextCardTitle}>Doctor Details</h3>
        </div>
        <div className="px-4 py-3">
          {isLoadingDoctorDetails ? (
            <p className="text-sm text-gray-500">Loading doctor details…</p>
          ) : doctorDetails ? (
            <dl className="space-y-0">
              <DetailRow label="User ID" value={doctorDetails.user_id}>
                <CopyValueButton value={doctorDetails.user_id} label="Copy user ID" />
              </DetailRow>
              <DetailRow label="Email" value={doctorDetails.user_email}>
                <CopyValueButton value={doctorDetails.user_email} label="Copy email" />
              </DetailRow>
              <DetailRow label="Organization" value={doctorDetails.organization} />
              <DetailRow label="Super org" value={doctorDetails.super_org} />
              <DetailRow label="Specialty" value={doctorDetails.specialty} />
              <DetailRow label="Phone" value={doctorDetails.phone_number} />
              <DetailRow label="NPI" value={doctorDetails.npi} />
              {isCapabilityEnabled("oncoemr") && (
                <DetailRow
                  label="Onco EMR Link"
                  value={
                    doctorDetails.onco_emr_url ? (
                      <a
                        href={doctorDetails.onco_emr_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ktx-purple underline break-all"
                      >
                        {doctorDetails.onco_emr_url}
                      </a>
                    ) : null
                  }
                />
              )}
              <DetailRow
                label="Created"
                value={
                  doctorDetails.created_at
                    ? new Date(doctorDetails.created_at).toLocaleString()
                    : null
                }
              />
              {isEditingModelType ? (
                <DetailRowShell label="Model type">
                  <select
                    id="doctorModelType"
                    aria-label="Model type"
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-100"
                    value={pendingModelType}
                    onChange={(e) => setPendingModelType(e.target.value)}
                    disabled={isSavingModelType}
                  >
                    {MODEL_TYPE_OPTIONS.map((option) => (
                      <option key={option.value || "empty"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="px-3 py-1 rounded bg-ktx-purple text-white text-sm hover:bg-purple-700 disabled:opacity-50"
                    onClick={handleSaveModelType}
                    disabled={isSavingModelType}
                  >
                    {isSavingModelType ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="text-sm font-medium text-ktx-purple hover:underline"
                    onClick={handleCancelModelTypeEdit}
                    disabled={isSavingModelType}
                  >
                    Cancel
                  </button>
                </DetailRowShell>
              ) : (
                <DetailRow
                  label="Model type"
                  value={getModelTypeDisplay(doctorDetails.model_type)}
                >
                  <button
                    type="button"
                    className="text-sm font-medium text-ktx-purple hover:underline"
                    onClick={handleStartModelTypeEdit}
                  >
                    Change
                  </button>
                </DetailRow>
              )}
            </dl>
          ) : (
            <p className="text-sm text-gray-500">Doctor details unavailable.</p>
          )}
        </div>
      </section>

      <section className={supportSubpanel} aria-label="Doctor schedule">
        <div className={supportSubpanelHeader}>
          <h3 className={supportContextCardTitle}>Doctor Schedule</h3>
        </div>
        <div className="px-4 py-4 space-y-4">
          <div className="space-y-3">
            <label
              htmlFor="doctorAppointmentDate"
              className="block text-[11px] font-medium uppercase tracking-wide text-gray-500"
            >
              Appointment date
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-sm text-gray-700"
                onClick={() => doctorDate && onDateChange(addDaysToDate(doctorDate, -1))}
                aria-label="Previous day"
              >
                ← Previous day
              </button>
              <input
                id="doctorAppointmentDate"
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={doctorDate}
                onChange={(e) => onDateChange(e.target.value)}
              />
              <button
                type="button"
                className="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-sm text-gray-700"
                onClick={() => doctorDate && onDateChange(addDaysToDate(doctorDate, 1))}
                aria-label="Next day"
              >
                Next day →
              </button>
            </div>
            {formattedDisplayDate && (
              <p className="text-sm text-gray-600">
                Showing patients for <span className="font-medium">{formattedDisplayDate}</span>
              </p>
            )}
          </div>

          {isLoadingPatients && (
            <p className="text-sm text-gray-500">Loading patients…</p>
          )}

          {!isLoadingPatients && doctorPatients.length === 0 && doctorDate && (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center">
              <p className="text-sm text-gray-600">
                No patients found for this doctor on this date.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Try another day using the date controls above.
              </p>
            </div>
          )}

          {!isLoadingPatients && doctorPatients.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                Select a patient ({doctorPatients.length} found)
              </p>
              <PatientSummaryTable
                patients={doctorPatients}
                onPatientIdClick={handlePatientClick}
                selectedPatientId={selectedPatientId}
                paginated
                showDeleteColumn={false}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DoctorPatientPicker;
